import React, { useState } from "react";
import { LifeBuoy, ChevronDown, ChevronUp, Send, Loader2, CheckCircle2, MessageCircle, Zap, Clock, ShieldCheck } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const FAQ = [
  {
    q: "The TV shows a pairing code but nothing else — what do I do?",
    a: "Open your dashboard on a phone or computer, go to Screens → New Screen, enter the 6-character code shown on the TV (or scan the QR code), and press Pair. As soon as it pairs the TV switches to your assigned design or a friendly placeholder."
  },
  {
    q: "My design isn't updating on the TV.",
    a: "The TV checks for updates every 5 seconds. If a change isn't showing up: (1) confirm you clicked Save inside the editor. (2) confirm the design is assigned to the screen on the Screens page. (3) if you just imported new content, wait 15 seconds. If it still doesn't refresh, unplug the stick, wait 10 seconds and plug it back in."
  },
  {
    q: "How do I switch a template between 16:9 (landscape) and 9:16 (portrait)?",
    a: "Open any design in the editor. In the top toolbar, click the 16:9 / 9:16 button — it flips the canvas orientation and re-lays your elements. Save the design and it will render in the new orientation on TV."
  },
  {
    q: "Can I use my own photos?",
    a: "Yes. Inside any editor, click the image element → Change image, then either upload from your computer, paste a URL, or pick from the built-in Pexels / Unsplash stock library. All uploads are stored in your Media Library so you can reuse them."
  },
  {
    q: "How do I add more screens?",
    a: "Every extra screen after the first is $4.99/month, billed on your existing Stripe subscription. Just pair a new TV in Screens → New Screen — the billing adjusts automatically on your next invoice."
  },
  {
    q: "How does the free 7-day trial work?",
    a: "When you register, you get 7 days of full Pro access (unlimited screens, all templates, AI generation). No card is required to start. Before the 7 days end, add a card in Billing to keep going, otherwise your account gracefully falls back to view-only until you subscribe."
  },
  {
    q: "The Android stick is stuck on the setup wizard / can't connect to Wi-Fi.",
    a: "This is very common on brand-new sticks. The Nexso launcher auto-opens the Wi-Fi settings when there's no connection. Tap the network you want, enter the password, and Nexso resumes automatically. If nothing happens: hold the remote's Home button for 3 seconds, then re-connect."
  },
  {
    q: "How do I update the launcher on all my sticks at once?",
    a: "The Android launcher auto-checks for a new version every day when connected to the internet. If a newer APK is available, it downloads and installs it silently. You don't need to touch the sticks — just upload the new APK in Admin → Launcher Releases."
  },
  {
    q: "Can I schedule which design shows at which time?",
    a: "Yes. On the Screens page, click ⏰ Schedule on a screen row. You can pick a design + start time + end time + which days of the week. Multiple rules per screen are supported."
  },
  {
    q: "I need something urgent — can I override the schedule right now?",
    a: "Yes. On the Screens page, click ⚡ Flash on a screen row. Pick a design (or playlist) + how long to keep it on (5 min – 24 h) and it takes over immediately. You can cancel the flash at any time."
  },
];

export default function Support() {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || user?.business_name || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [openIdx, setOpenIdx] = useState(0);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!subject.trim() || !message.trim()) {
      toast.error("Please add a subject and a message.");
      return;
    }
    if (!email.trim()) {
      toast.error("Please add an email so we can reply.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/support/message", {
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim(),
        name: name.trim(),
        category,
        page_url: "/dashboard/support",
      });
      setSent(r.data);
      toast.success(`Message sent · ticket ${r.data.ticket_id}`);
      setSubject(""); setMessage("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-10">
        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-sky-500 via-sky-400 to-cyan-400 text-white p-6 sm:p-10 mb-8 shadow-lg" data-testid="support-hero">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm shrink-0">
              <LifeBuoy className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                Help Center
              </h1>
              <p className="mt-2 text-sm sm:text-base text-sky-50 max-w-2xl">
                Real humans. No bots. Every message reaches a founder directly and we usually
                reply within 30 minutes during business hours.
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold">
                <span className="inline-flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full"><Zap className="h-3 w-3" />Direct-to-founder</span>
                <span className="inline-flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full"><Clock className="h-3 w-3" />~30 min reply</span>
                <span className="inline-flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full"><ShieldCheck className="h-3 w-3" />Nexso-owned support</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_460px] gap-8">
          {/* FAQ */}
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              Frequently asked
            </h2>
            <div className="space-y-2" data-testid="support-faq">
              {FAQ.map((item, i) => {
                const isOpen = openIdx === i;
                return (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <button
                      onClick={() => setOpenIdx(isOpen ? -1 : i)}
                      className="w-full flex items-center justify-between text-left px-4 py-3.5 hover:bg-slate-50 transition-colors"
                      data-testid={`faq-${i}`}
                    >
                      <span className="text-sm font-semibold text-slate-900 pr-4">{item.q}</span>
                      {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Contact form */}
          <div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:p-6 shadow-sm sticky top-6" data-testid="support-form-card">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="h-5 w-5 text-sky-500" />
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Message us
                </h2>
              </div>
              {sent ? (
                <div className="text-center py-6" data-testid="support-form-success">
                  <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <div className="text-base font-bold text-slate-900">Ticket {sent.ticket_id}</div>
                  <div className="text-sm text-slate-500 mt-1">
                    Sent · we&apos;ll reply at <span className="font-medium text-slate-700">{email}</span>
                  </div>
                  <Button
                    variant="outline"
                    className="mt-4 rounded-full"
                    onClick={() => setSent(null)}
                    data-testid="support-form-new"
                  >
                    Send another
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Your name</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-9 text-sm" placeholder="John Baker" data-testid="support-page-name-input" />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Email *</Label>
                      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-9 text-sm" placeholder="you@business.com" data-testid="support-page-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Category</Label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger className="mt-1 h-9 text-sm" data-testid="support-page-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General question</SelectItem>
                        <SelectItem value="bug">Something isn&apos;t working</SelectItem>
                        <SelectItem value="setup">TV / Stick setup</SelectItem>
                        <SelectItem value="design">Design / template help</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="feature">Feature request</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Subject *</Label>
                    <Input required value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-9 text-sm" placeholder="e.g. TV shows blank white screen" data-testid="support-page-subject-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Message *</Label>
                    <textarea
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us what's happening…"
                      className="mt-1 w-full min-h-[140px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400"
                      data-testid="support-page-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full h-11 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-sm"
                    data-testid="support-page-send-btn"
                  >
                    {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : <><Send className="h-4 w-4 mr-2" />Send message</>}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
